import React, { useState, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../hooks/UserContext";
import { api } from "../../services/baseUrl";
import { ThemeContext } from "../../hooks/ThemeContext";

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: "", password: "" } });

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { login } = useContext(UserContext);
  const emailRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    emailRef.current?.focus(); // focus direct sur l'email
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    setAuthError(null);
    try {
      const formData = new FormData();
      formData.append("email", data.email);
      formData.append("password", data.password);

      const res = await api.post("/login", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200) {
        localStorage.setItem("access_token", res.data.data.access_token.token);
        login(res.data.user);
        navigate(from, { replace: true });
      }
    } catch (err) {
      setAuthError("Échec de la connexion. Veuillez vérifier vos identifiants.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex items-center justify-center min-h-screen ${isDark ? "bg-[#0f1114]" : "bg-slate-50"} px-4`}>
      <div className={`w-full max-w-md ${isDark ? "bg-[#0b0b0c] text-white border-white/10" : "bg-white/95 text-gray-800 border-gray-200"} rounded-xl shadow-lg p-8 border`}>
        <h1 className={`text-2xl font-bold text-center mb-6 ${isDark ? "text-white" : "text-[#1d3557]"}`}>
          Connexion
        </h1>

        {/* Message développement */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? "bg-amber-500/10 border-amber-500/30 text-amber-400" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-sm">Fonctionnalité en développement</p>
              <p className="text-xs mt-1">L'authentification est actuellement indisponible. Le site est en cours de développement.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className={`block text-sm font-medium ${isDark ? "text-gray-300" : "text-[#3f555d]"}`}>
              Email :
            </label>
            <input
              id="email"
              type="email"
              ref={emailRef}
              {...register("email", { required: "Adresse mail obligatoire" })}
              className={`mt-1 w-full px-4 py-2 border rounded-md shadow-sm ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-[#eef3f5] border-[#a7b4b7] text-[#3f555d]"} focus:outline-none focus:ring-2 focus:ring-rose-500`}
            />
            {errors.email && <p className="text-sm text-rose-500 mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="password" className={`block text-sm font-medium ${isDark ? "text-gray-300" : "text-[#3f555d]"}`}>
              Mot de passe :
            </label>
            <input
              id="password"
              type="password"
              {...register("password", {
                required: "Mot de passe obligatoire",
              })}
              className={`mt-1 w-full px-4 py-2 border rounded-md shadow-sm ${isDark ? "bg-[#0b0b0c] border-white/10 text-white" : "bg-[#eef3f5] border-[#a7b4b7] text-[#3f555d]"} focus:outline-none focus:ring-2 focus:ring-rose-500`}
            />
            {errors.password && <p className="text-sm text-rose-500 mt-1">{errors.password.message}</p>}
          </div>

          {authError && <p className="text-sm text-rose-500">{authError}</p>}

          <div className="flex items-center justify-between gap-2">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-2 px-4 text-white font-semibold rounded-md shadow-md transition duration-200 ${isDark ? "bg-rose-500 hover:bg-rose-600" : "bg-rose-600 hover:bg-rose-700"}`}>
              {loading ? "Connexion..." : "Connexion"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/register")}
              className={`flex-1 py-2 px-4 font-semibold rounded-md transition duration-200 ${isDark ? "bg-transparent text-rose-300 border border-white/10 hover:bg-white/5" : "bg-transparent text-rose-600 border border-rose-600 hover:bg-rose-50"}`}>
              Pas encore inscrit ? 😎
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
